import { Link } from "@inertiajs/react"
import { Check, ChevronRight, Lock, Play } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MasterclassModuleCover } from "@/components/masterclasses/masterclass-module-cover"
import { learnMasterclassModulePath } from "@/routes"
import type {
  CreatorLearnModuleRow,
  CreatorLearnModuleState,
} from "@/types/learn"

/**
 * OKS-602 — Creator-side Modules section on `Creator/Learn/Show`.
 *
 * Renders the ordered module list with derived per-module state
 * (completed / in-progress / locked) + a text progress summary. State is
 * computed server-side (`Masterclass::ModuleProgress`) — this component
 * only renders it. The sequential-unlock rule is enforced on the server;
 * the UI's non-linking of locked rows is a convenience, NOT the control.
 *
 * Rows (UX Spec §2):
 *   - completed / in-progress → Inertia `<Link>` to the module view,
 *     with a trailing chevron.
 *   - locked → non-interactive (`aria-disabled`, non-focusable), lock
 *     icon + text label; the FIRST locked row shows helper copy
 *     ("Complete the previous module to unlock") — UX U2.
 *
 * A11y (UX Spec §6): state is conveyed by icon AND text label (never
 * colour alone); the progress summary is a plain SR-readable string.
 */
export interface CreatorMasterclassModulesSectionProps {
  masterclassId: number
  modules: CreatorLearnModuleRow[]
  completedCount: number
}

export function CreatorMasterclassModulesSection({
  masterclassId,
  modules,
  completedCount,
}: CreatorMasterclassModulesSectionProps) {
  const { t } = useTranslation()

  // Index of the first locked row — only that row shows the helper copy.
  const firstLockedIndex = modules.findIndex((m) => m.state === "locked")

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="learn-modules-section"
      aria-label={t("learn.modules.title")}
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold tracking-tight">
          {t("learn.modules.title")}
        </h2>
        <p
          className="text-muted-foreground text-sm"
          data-testid="learn-modules-progress-summary"
        >
          {t("learn.modules.progress_summary", {
            completed: completedCount,
            total: modules.length,
          })}
        </p>
      </div>

      <ol className="flex flex-col gap-2">
        {modules.map((mod, index) => (
          <li key={mod.id} data-testid={`learn-module-row-${mod.id}`}>
            <ModuleRow
              masterclassId={masterclassId}
              module={mod}
              showLockedHelper={index === firstLockedIndex}
            />
          </li>
        ))}
      </ol>
    </section>
  )
}

// ---------------------------------------------------------------------

function ModuleRow({
  masterclassId,
  module: mod,
  showLockedHelper,
}: {
  masterclassId: number
  module: CreatorLearnModuleRow
  showLockedHelper: boolean
}) {
  const { t } = useTranslation()
  const isLocked = mod.state === "locked"

  const inner = (
    <>
      <span className="text-muted-foreground w-6 shrink-0 text-center text-sm font-medium tabular-nums">
        {mod.position + 1}
      </span>
      {/* OKS-833 — leading square thumb (56px, `:thumb` variant; swatch
          when none). Read-only; keeps the list light (no cover band). */}
      <MasterclassModuleCover
        id={mod.id}
        coverImageUrl={mod.cover_image_url}
        title={mod.title}
        size="thumb"
        data-testid={`learn-module-row-${mod.id}-thumb`}
      />
      <StateIndicator moduleId={mod.id} state={mod.state} />
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 text-sm font-medium">{mod.title}</span>
        {isLocked && showLockedHelper && (
          <span className="text-muted-foreground mt-0.5 block text-xs">
            {t("learn.modules.locked_helper")}
          </span>
        )}
      </span>
      {!isLocked && (
        <ChevronRight
          className="text-muted-foreground size-4 shrink-0"
          aria-hidden="true"
        />
      )}
    </>
  )

  const rowClass =
    "flex items-center gap-3 rounded-md border p-3 min-h-11 transition-colors"

  if (isLocked) {
    return (
      <div
        className={`${rowClass} bg-muted/40 text-muted-foreground`}
        aria-disabled="true"
      >
        {inner}
      </div>
    )
  }

  return (
    <Link
      href={learnMasterclassModulePath(masterclassId, mod.id)}
      className={`${rowClass} hover:bg-accent focus-visible:ring-ring focus:outline-none focus-visible:ring-2`}
      data-testid={`learn-module-row-${mod.id}-link`}
    >
      {inner}
    </Link>
  )
}

function StateIndicator({
  moduleId,
  state,
}: {
  moduleId: number
  state: CreatorLearnModuleState
}) {
  const { t } = useTranslation()

  const config: Record<
    CreatorLearnModuleState,
    { icon: typeof Check; className: string; labelKey: string }
  > = {
    completed: {
      icon: Check,
      className: "text-emerald-600",
      labelKey: "learn.modules.state.completed",
    },
    in_progress: {
      icon: Play,
      className: "text-primary",
      labelKey: "learn.modules.state.in_progress",
    },
    locked: {
      icon: Lock,
      className: "text-muted-foreground",
      labelKey: "learn.modules.state.locked",
    },
  }

  const { icon: Icon, className, labelKey } = config[state]

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${className}`}
      data-testid={`learn-module-row-${moduleId}-state`}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span>{t(labelKey)}</span>
    </span>
  )
}
